// Dead-reckoning for the 3D drone between telemetry fixes.
//
// Fixes arrive at 1-4 Hz (the browser gets a frame every 250 ms, but a real
// drone over MQTT only has new data every ~1 s). Easing the model toward the
// last fix left it trailing the drone and moving in steps. Instead each frame
// draws where the drone should be NOW: the last fix moved on by its velocity
// for the time since it arrived. The velocity is measured between fixes, so
// it is the course actually flown (a multirotor's heading is not its course),
// and the climb rate comes from the autopilot when it is sent.
//
// A fix is "new" only when its position or height changed — the same fix
// re-broadcast every 250 ms must not restart the clock (that pulled the model
// back to the stale point four times a second). Extrapolation stops after
// about one fix interval, so a drone that stopped is overshot by at most one
// interval's travel before the next fix corrects it.

const M_PER_DEG_LAT = 111320;
const MAX_INTERVAL_S = 3;     // fixes further apart than this are not a velocity
const MAX_AHEAD_S = 1.2;      // never extrapolate further than this

const mPerDegLon = (lat) => M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);

/**
 * @param {object|null} prev   previous return value (or null)
 * @param {{lat:number, lon:number, agl:?number, climbRate:?number}} fix
 * @param {number} nowMs       arrival time (same clock as predict's nowMs)
 * @returns {object} tracked fix: { lat, lon, agl, at, vE, vN, vU, intervalS }
 */
export function trackFix(prev, { lat, lon, agl, climbRate }, nowMs) {
  const hasAgl = Number.isFinite(agl);
  if (prev
    && Math.abs(prev.lat - lat) < 1e-8 && Math.abs(prev.lon - lon) < 1e-8
    && (hasAgl ? Number.isFinite(prev.agl) && Math.abs(prev.agl - agl) < 0.005 : !Number.isFinite(prev.agl))) {
    return prev;  // the same fix again
  }
  const dt = prev ? (nowMs - prev.at) / 1000 : 0;
  const usable = prev && dt > 0.05 && dt <= MAX_INTERVAL_S;
  const vE = usable ? ((lon - prev.lon) * mPerDegLon(lat)) / dt : 0;
  const vN = usable ? ((lat - prev.lat) * M_PER_DEG_LAT) / dt : 0;
  let vU = 0;
  if (Number.isFinite(climbRate)) vU = climbRate;
  else if (usable && hasAgl && Number.isFinite(prev.agl)) vU = (agl - prev.agl) / dt;
  // Smoothed spacing between fixes: how far ahead it is worth predicting.
  const intervalS = usable ? (prev.intervalS ? 0.7 * prev.intervalS + 0.3 * dt : dt) : (prev ? prev.intervalS : null);
  return { lat, lon, agl: hasAgl ? agl : null, at: nowMs, vE, vN, vU, intervalS };
}

/** Where the drone should be at nowMs: { lat, lon, agl }. */
export function predict(fix, nowMs) {
  if (!fix) return null;
  const horizon = Math.min(MAX_AHEAD_S, (fix.intervalS || 0.5) * 1.2);
  const t = Math.max(0, Math.min(horizon, (nowMs - fix.at) / 1000));
  return {
    lat: fix.lat + (fix.vN * t) / M_PER_DEG_LAT,
    lon: fix.lon + (fix.vE * t) / mPerDegLon(fix.lat),
    agl: Number.isFinite(fix.agl) ? Math.max(0, fix.agl + fix.vU * t) : null,
  };
}
